import Link from 'next/link'
import { ArrowUpRight, Clock3, Mail, MapPin, Phone, ShieldCheck } from 'lucide-react'
import Seo from '../Seo'
import { useI18nSafe } from '../../context/I18nContext'

const iconClass = 'h-4 w-4'

export default function LegalDocumentPage({ pageKey }) {
  const { t } = useI18nSafe()
  const copy = t(`legalPages.${pageKey}`)

  return (
    <div className="bg-[var(--dash-blacker)] text-white">
      <Seo title={copy.metaTitle} description={copy.metaDescription} path={copy.path} />

      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top_left,rgba(255,93,16,0.18),transparent_40%),radial-gradient(circle_at_top_right,rgba(228,188,155,0.12),transparent_32%),linear-gradient(180deg,var(--dash-black)_0%,var(--dash-blacker)_100%)]" />
        <div className="absolute inset-0 z-0 opacity-[0.04] bg-[url('/bg/5.webp')] bg-cover bg-center" />
        <div className="absolute left-1/2 top-0 z-0 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-[var(--dash-main)]/12 blur-[120px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.82fr)_minmax(280px,0.18fr)] lg:items-end">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-[var(--dash-main)]/25 bg-[var(--dash-main)]/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.28em] text-[var(--dash-main)]">
                <ShieldCheck className="h-4 w-4" />
                {copy.eyebrow}
              </div>
              <h1 className="mt-7 text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] text-white sm:text-5xl lg:text-6xl">
                {copy.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                {copy.description}
              </p>
            </div>

            <div className="hidden justify-self-end lg:block">
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[var(--dash-main)] shadow-[0_0_40px_rgba(255,93,16,0.12)]">
                <ShieldCheck className="h-8 w-8" />
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-3">
            <InfoPill icon={<Clock3 className={iconClass} />} label={copy.lastUpdatedLabel} value={copy.lastUpdated} />
            <InfoPill icon={<Mail className={iconClass} />} label={copy.emailLabel} value="sales@etc.tn" />
            <InfoPill icon={<Phone className={iconClass} />} label={copy.phoneLabel} value="+216 26 363 025" />
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-6 py-14 lg:grid-cols-[minmax(0,0.72fr)_minmax(280px,0.28fr)] lg:px-10 lg:py-20">
        <div className="space-y-5">
          {copy.sections.map((section, index) => (
            <article
              key={section.title}
              className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[var(--dash-main)]/25 hover:bg-white/[0.045] sm:p-8"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--dash-main)]/20 bg-[var(--dash-main)]/10 text-sm font-black text-[var(--dash-main)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-black uppercase tracking-tight text-white">{section.title}</h2>
                  <div className="mt-5 space-y-4 text-sm leading-7 text-slate-300 sm:text-base">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.items ? (
                      <ul className="space-y-3">
                        {section.items.map((item) => (
                          <li key={item} className="flex gap-3">
                            <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--dash-main)]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
          <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <h2 className="text-sm font-black uppercase tracking-[0.18em] text-white">{copy.contactTitle}</h2>
            <div className="mt-5 space-y-4 text-sm leading-6 text-slate-300">
              <ContactLine icon={<MapPin className={iconClass} />}>
                Avenue El Arbi Zarrouk, 8000 Nabeul - Tunisie
              </ContactLine>
              <ContactLine icon={<Phone className={iconClass} />}>
                <a href="tel:+21626363025" className="transition-colors hover:text-white">
                  +216 26 363 025
                </a>
              </ContactLine>
              <ContactLine icon={<Mail className={iconClass} />}>
                <a href="mailto:sales@etc.tn" className="transition-colors hover:text-white">
                  sales@etc.tn
                </a>
              </ContactLine>
            </div>
          </section>

          <section className="rounded-2xl border border-[var(--dash-main)]/25 bg-[linear-gradient(180deg,rgba(255,93,16,0.11),rgba(255,93,16,0.05))] p-6">
            <h2 className="text-sm font-black uppercase tracking-[0.18em] text-white">{copy.nextTitle}</h2>
            <p className="mt-4 text-sm leading-6 text-slate-300">{copy.nextDescription}</p>
            <Link
              href="/contact"
              className="mt-6 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.14em] text-[var(--dash-main)] transition-colors hover:text-white"
            >
              {copy.contactLink}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </section>
        </aside>
      </section>
    </div>
  )
}

function InfoPill({ icon, label, value }) {
  return (
    <div className="flex min-h-[92px] items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--dash-main)]/15 text-[var(--dash-main)]">
        {icon}
      </span>
      <span>
        <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{label}</span>
        <span className="mt-1 block text-sm font-bold text-white">{value}</span>
      </span>
    </div>
  )
}

function ContactLine({ icon, children }) {
  return (
    <div className="flex gap-3">
      <span className="mt-1 shrink-0 text-[var(--dash-main)]">{icon}</span>
      <span>{children}</span>
    </div>
  )
}
